/** @typedef {'reseller'|'franchisor'|'manufacturer'|'association'|'carrier'|'generic'} PartnerProfileType */

export const PARTNER_PROFILE_TYPES = /** @type {const} */ ([
  "reseller",
  "franchisor",
  "manufacturer",
  "association",
  "carrier",
  "generic",
]);

const DEFAULT_CAPABILITIES = {
  partnerPortal: true,
  managePricing: false,
  createWorkspace: false,
  viewFinancial: false,
  viewMargin: false,
  viewPlansAndBilling: false,
  viewAnalytics: true,
  viewLiveDashboard: true,
  viewPresence: true,
  viewTeam: true,
  viewPassengerSales: false,
};

/** @type {Record<PartnerProfileType, { capabilities: Record<string, boolean>, dashboardWidgets: string[] }>} */
export const PARTNER_PROFILE_DEFINITIONS = {
  reseller: {
    capabilities: {
      managePricing: true,
      createWorkspace: true,
      viewFinancial: true,
      viewMargin: true,
      viewPlansAndBilling: true,
    },
    dashboardWidgets: [
      "kpiTotal",
      "kpiUsageTrend",
      "kpiOnline",
      "financialSummary",
      "adoptionRate",
      "attentionAlerts",
      "kindMix",
      "usageDailyChart",
      "topUsage",
      "atRiskList",
      "cohorts",
      "nextSteps",
    ],
  },
  franchisor: {
    capabilities: { createWorkspace: true },
    dashboardWidgets: [
      "kpiTotal",
      "kpiUsageTrend",
      "kpiOnline",
      "adoptionRate",
      "attentionAlerts",
      "operationalMix",
      "usageDailyChart",
      "topUsage",
      "atRiskList",
      "nextSteps",
    ],
  },
  manufacturer: {
    capabilities: { createWorkspace: true },
    dashboardWidgets: [
      "kpiTotal",
      "kpiUsageTrend",
      "kpiOnline",
      "adoptionRate",
      "attentionAlerts",
      "operationalMix",
      "usageDailyChart",
      "segmentBreakdown",
      "topUsage",
      "atRiskList",
      "nextSteps",
    ],
  },
  association: {
    capabilities: {},
    dashboardWidgets: [
      "kpiTotal",
      "kpiUsageTrend",
      "kpiOnline",
      "adoptionRate",
      "attentionAlerts",
      "operationalMix",
      "usageDailyChart",
      "topUsage",
      "atRiskList",
      "nextSteps",
    ],
  },
  carrier: {
    capabilities: { createWorkspace: true, viewPassengerSales: true },
    dashboardWidgets: [
      "kpiTotal",
      "kpiUsageTrend",
      "kpiOnline",
      "adoptionRate",
      "attentionAlerts",
      "operationalMix",
      "usageDailyChart",
      "segmentBreakdown",
      "topUsage",
      "atRiskList",
      "nextSteps",
    ],
  },
  generic: {
    capabilities: { createWorkspace: true },
    dashboardWidgets: [
      "kpiTotal",
      "kpiUsageTrend",
      "kpiOnline",
      "adoptionRate",
      "attentionAlerts",
      "operationalMix",
      "usageDailyChart",
      "topUsage",
      "atRiskList",
      "nextSteps",
    ],
  },
};

/**
 * @param {string|undefined|null} raw
 * @returns {PartnerProfileType}
 */
export function normalizePartnerProfileType(raw) {
  const t = String(raw || "reseller")
    .trim()
    .toLowerCase();
  return /** @type {PartnerProfileType} */ (
    PARTNER_PROFILE_TYPES.includes(/** @type {PartnerProfileType} */ (t)) ? t : "reseller"
  );
}

/**
 * @param {Record<string, unknown>|null|undefined} profileFromApi
 * @param {Record<string, unknown>|null|undefined} reseller
 */
export function resolvePartnerProfileFromContext(profileFromApi, reseller) {
  if (profileFromApi && typeof profileFromApi === "object" && profileFromApi.type) {
    return profileFromApi;
  }
  const type = normalizePartnerProfileType(
    /** @type {string|undefined} */ (reseller?.profile_type)
  );
  const def = PARTNER_PROFILE_DEFINITIONS[type] || PARTNER_PROFILE_DEFINITIONS.reseller;
  return {
    type,
    terminology: {},
    capabilities: { ...DEFAULT_CAPABILITIES, ...def.capabilities },
    dashboardWidgets: [...def.dashboardWidgets],
  };
}

/**
 * @param {Record<string, unknown>|null|undefined} profile
 * @param {string} widgetId
 */
export function partnerProfileHasWidget(profile, widgetId) {
  const widgets = profile?.dashboardWidgets;
  if (!Array.isArray(widgets)) return true;
  return widgets.includes(widgetId);
}

/** Apenas perfil revendedor vê planos, valores e repasses. */
export function partnerCanViewCommercialBilling(profile, reseller) {
  return normalizePartnerProfileType(profile?.type || reseller?.profile_type) === "reseller";
}
